import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Tooltip,
  Avatar,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import CreateUser from './CreateUser';
import proxy from '../../api/axios';
import { resolveProfileImage } from '../../common/utils';
import {
  ACCENT,
  AdminCard,
  AdminEmpty,
  AdminLoading,
  AdminPage,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbar,
  MetaChip,
  AdminFormDialog,
  roleColor,
  useAdminPageTheme,
} from './adminUi';

const ROLE_LABELS = {
  admin: 'Admin',
  softadmin: 'Soft Admin',
  user: 'User',
};

const ManageUsers = () => {
  const { border, muted, isDark } = useAdminPageTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get('/users/all');
      setUsers(data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const stats = useMemo(() => {
    const byRole = users.reduce((acc, user) => {
      const role = user.role || 'user';
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});
    return {
      total: users.length,
      admin: byRole.admin || 0,
      softadmin: byRole.softadmin || 0,
      user: byRole.user || 0,
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false;
      if (!q) return true;
      return (
        user._id?.toLowerCase().includes(q) ||
        user.name?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        user.role?.toLowerCase().includes(q) ||
        user.username?.toLowerCase().includes(q)
      );
    });
  }, [users, searchTerm, roleFilter]);

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      const { data } = await proxy.delete(`/users/${userToDelete._id}`);
      toast.success(data.message || 'User deleted');
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
      console.error(error);
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    fetchUsers();
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      borderRadius: 2.5,
      border: `1px solid ${border}`,
    },
  };

  return (
    <AdminPage>
      <Helmet>
        <title>Manage Users | Admin | CrafterHack</title>
      </Helmet>

      <AdminPageHeader
        title="Manage users"
        subtitle="Create accounts, update roles, and manage platform access"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchUsers} sx={{ border: `1px solid ${border}` }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setSelectedUser(null);
                setOpenDialog(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none' }}
            >
              Create user
            </Button>
          </>
        }
      />

      <AdminStatGrid
        items={[
          { label: 'TOTAL', value: stats.total },
          { label: 'ADMINS', value: stats.admin, color: '#cf222e' },
          { label: 'SOFT ADMINS', value: stats.softadmin, color: '#d97706' },
          { label: 'USERS', value: stats.user, color: ACCENT },
        ]}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {[
          { id: 'all', label: `All (${stats.total})` },
          { id: 'admin', label: `Admin (${stats.admin})` },
          { id: 'softadmin', label: `Soft admin (${stats.softadmin})` },
          { id: 'user', label: `User (${stats.user})` },
        ].map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={roleFilter === item.id ? 'contained' : 'outlined'}
            onClick={() => setRoleFilter(item.id)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              bgcolor: roleFilter === item.id ? ACCENT : 'transparent',
              borderColor: roleFilter === item.id ? ACCENT : border,
              boxShadow: 'none',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Box>

      <AdminToolbar>
        <TextField
          size="small"
          placeholder="Search name, email, username, role…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
      </AdminToolbar>

      {loading ? (
        <AdminLoading />
      ) : filteredUsers.length === 0 ? (
        <AdminEmpty
          title="No users found"
          description="Try another search or create a new user."
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredUsers.map((user) => {
            const roleLabel = ROLE_LABELS[user.role] || user.role || 'User';
            const initials = (user.name?.[0] || user.email?.[0] || 'U').toUpperCase();

            return (
              <AdminCard
                key={user._id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0, flex: 1 }}>
                  <Avatar
                    src={resolveProfileImage(user.profileImage)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      flexShrink: 0,
                      fontWeight: 800,
                      bgcolor: `${roleColor(user.role)}22`,
                      color: roleColor(user.role),
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                      <MetaChip
                        label={roleLabel}
                        color={roleColor(user.role)}
                        isDark={isDark}
                      />
                      {user.username && (
                        <MetaChip label={`@${user.username}`} color="#2563eb" isDark={isDark} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <PersonOutlineIcon sx={{ fontSize: 16, color: muted }} />
                      <Typography sx={{ fontWeight: 800, fontSize: '0.98rem' }}>
                        {user.name || 'Unnamed user'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem', mt: 0.35 }}>
                      {user.email}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: muted,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.68rem',
                      }}
                    >
                      {user._id}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedUser(user);
                        setOpenDialog(true);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setUserToDelete(user)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AdminCard>
            );
          })}
        </Box>
      )}

      <AdminFormDialog
        open={openDialog}
        onClose={closeDialog}
        title={selectedUser ? 'Edit user' : 'Create user'}
        subtitle={
          selectedUser
            ? `Update access for ${selectedUser.name || selectedUser.email}`
            : 'Add a new account to the platform'
        }
        icon={selectedUser ? PersonOutlineIcon : PersonAddOutlinedIcon}
        formId={CreateUser.formId}
        submitLabel={selectedUser ? 'Save changes' : 'Create user'}
        loading={formSaving}
        maxWidth="sm"
      >
        <CreateUser
          user={selectedUser}
          onClose={closeDialog}
          onSavingChange={setFormSaving}
        />
      </AdminFormDialog>

      <Dialog
        open={Boolean(userToDelete)}
        onClose={() => setUserToDelete(null)}
        sx={dialogSx}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {userToDelete
              ? `Remove ${userToDelete.name || userToDelete.email}? This cannot be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setUserToDelete(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default ManageUsers;
